import {
  BuilderTier,
  CalculateCiRequest,
  CiCalculationResult,
  CiRecord,
  LegalIssueStatus,
  LocalityRating,
  SamePropertyOverpricingRating,
  ScarcityRating,
  TransactionRow,
} from '../../../_services/ci.service';
import { isLandLed, isSupplyOverrideComplete, needsBuildingRate } from '../config/ci-options.config';
import { CiFormValue, PropertyCategory } from '../models/ci-form.model';
import { CiQuestion } from '../models/ci-question.model';
import { BenchmarkBasis, VerdictTone } from '../models/ci-result.model';

/**
 * Checklist questions arrive from the API with admin-typed wording, so they are
 * matched on meaning and mapped onto the
 * fixed fields the calculate endpoint takes.
 */
const CHECKLIST_MATCHERS: Record<
  | 'locality'
  | 'builderTier'
  | 'legalIssueStatus'
  | 'scarcity'
  | 'samePropertyOverpricing',
  RegExp
> = {
  locality: /locality|location/i,
  builderTier: /builder/i,
  legalIssueStatus: /legal|title|\boc\b|approval/i,
  scarcity: /scarc/i,
  samePropertyOverpricing: /overpric|over-priced|asking|quoted/i,
};

/** Values the CI API accepts for each checklist field. */
const CHECKLIST_VALUES: Record<keyof typeof CHECKLIST_MATCHERS, string[]> = {
  locality: Object.values(LocalityRating),
  builderTier: Object.values(BuilderTier),
  legalIssueStatus: Object.values(LegalIssueStatus),
  scarcity: Object.values(ScarcityRating),
  samePropertyOverpricing: Object.values(SamePropertyOverpricingRating),
};

const CHECKLIST_NAMES: Record<keyof typeof CHECKLIST_MATCHERS, string> = {
  locality: 'Locality',
  builderTier: 'Builder Tier',
  legalIssueStatus: 'Legal Issue',
  scarcity: 'Scarcity',
  samePropertyOverpricing: 'Same Property Overpricing',
};

const normalise = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '');

/** Snaps a label onto the exact value the API's enum expects (ignores case, spacing, punctuation). */
function toApiValue(field: keyof typeof CHECKLIST_MATCHERS, label: string | undefined): string | undefined {
  if (!label) return undefined;
  const value = normalise(label);
  const exact = CHECKLIST_VALUES[field].find((v) => normalise(v) === value);
  if (exact) return exact;

  // Question options are admin-editable, so older or reworded labels (for
  // example "Prime" or "Above") are translated onto the API's enum values.
  switch (field) {
    case 'locality':
      if (/negative|bad/.test(value)) return LocalityRating.NEGATIVE;
      if (/weak|poor|below/.test(value)) return LocalityRating.WEAK;
      if (/premium|strong|prime/.test(value)) return LocalityRating.PREMIUM;
      if (/good/.test(value)) return LocalityRating.GOOD;
      if (/average|neutral/.test(value)) return LocalityRating.AVERAGE;
      break;
    case 'builderTier':
      if (/poor|delivery/.test(value)) return BuilderTier.POOR;
      if (/tier1/.test(value)) return BuilderTier.TIER_1;
      if (/tier2/.test(value)) return BuilderTier.TIER_2;
      if (/tier3|average/.test(value)) return BuilderTier.AVERAGE;
      if (/unknown/.test(value)) return BuilderTier.UNKNOWN;
      break;
    case 'legalIssueStatus':
      if (/major|unclear/.test(value)) return LegalIssueStatus.MAJOR_LEGAL_ISSUE;
      if (/nooc|approvalconcern/.test(value)) return LegalIssueStatus.NO_OC_APPROVAL_CONCERN;
      if (/minor|documentation/.test(value)) return LegalIssueStatus.MINOR_DOCUMENTATION_GAP;
      if (/pending/.test(value)) return LegalIssueStatus.CLEAR_TITLE_OC_PENDING;
      if (/clear|noissue/.test(value)) return LegalIssueStatus.CLEAR_TITLE_OC_AVAILABLE;
      break;
    case 'scarcity':
      if (/rare|hardtoreplace|high/.test(value)) return ScarcityRating.RARE;
      if (/limited|medium/.test(value)) return ScarcityRating.LIMITED;
      if (/normal|low/.test(value)) return ScarcityRating.NORMAL;
      if (/many|none/.test(value)) return ScarcityRating.MANY;
      break;
    case 'samePropertyOverpricing':
      if (/notoverpriced|inline|below/.test(value)) return SamePropertyOverpricingRating.IN_LINE;
      if (/very|unjustified/.test(value)) return SamePropertyOverpricingRating.VERY;
      if (/highly/.test(value)) return SamePropertyOverpricingRating.HIGHLY;
      if (/moderately|above/.test(value)) return SamePropertyOverpricingRating.MODERATELY;
      if (/slightly/.test(value)) return SamePropertyOverpricingRating.SLIGHTLY;
      break;
  }

  return label.trim();
}

function selectedLabel(
  questions: CiQuestion[],
  answers: Record<string, string>,
  matcher: RegExp,
): string | undefined {
  const question = questions.find((q) => matcher.test(q.label));
  const value = question ? answers?.[question.key] : undefined;
  return question?.options.find((o) => o.value === value)?.label;
}

/**
 * Saved record → checklist answers. The record stores API enum values; the form
 * holds option values keyed by question, so each option is matched by the API
 * value its label translates to (labels may differ from the stored value).
 */
export function answersFromRecord(
  record: Partial<Record<keyof typeof CHECKLIST_MATCHERS, string | null>>,
  questions: CiQuestion[],
): Record<string, string> {
  const answers: Record<string, string> = {};
  for (const field of Object.keys(CHECKLIST_MATCHERS) as (keyof typeof CHECKLIST_MATCHERS)[]) {
    const label = record[field];
    const question = questions.find((q) => CHECKLIST_MATCHERS[field].test(q.label));
    const option = label
      ? question?.options.find((o) => o.label === label)
        ?? question?.options.find((o) => toApiValue(field, o.label) === toApiValue(field, label))
      : undefined;
    if (question && option) answers[question.key] = option.value;
  }
  return answers;
}

/** Number inputs can hold numeric strings; the API only takes numbers. */
function numberOrNull(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function positive(value: number | null | undefined): number | undefined {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const MAX_TRANSACTION_ROWS = 5;

/** "2026-09-30" → "30 Sep 2026", the format the CI API stores and echoes back. */
function toApiDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]} ${MONTHS[Number(m[2]) - 1]} ${m[1]}` : iso;
}

/** Sent when an analyst leaves a filled row's source blank, so the API never gets null for it. */
const DEFAULT_TRANSACTION_SOURCE: Record<CiFormValue['benchmarking']['source'], string> = {
  transactions: 'Property transaction',
  'similar-listings': 'Similar listed property',
  anchor: 'Anchor property',
};

export function usableTransactions(value: CiFormValue): TransactionRow[] {
  const fallbackSource = DEFAULT_TRANSACTION_SOURCE[value.benchmarking.source] ?? DEFAULT_TRANSACTION_SOURCE.transactions;
  return (value.benchmarking.transactions ?? [])
    .filter((t) => positive(t.psf) !== undefined && !!t.date)
    .map((t) => ({
      price: Number(t.psf),
      source: t.source?.trim() || fallbackSource,
      date: toApiDate(t.date),
    }));
}

/** Always five rows; unused ones go as explicit nulls. */
function lastFiveTransactions(value: CiFormValue): TransactionRow[] {
  const rows = usableTransactions(value).slice(0, MAX_TRANSACTION_ROWS);
  while (rows.length < MAX_TRANSACTION_ROWS)
    rows.push({ price: null, source: null, date: null });
  return rows;
}

/** Marks the synthetic benchmark row that carries a plot/house Land Rate. */
export const LAND_RATE_SOURCE = 'Land rate benchmark';

/** The Land Rate a plot/house record was calculated with, if it is one. */
export function landRateFromRecord(record: CiRecord): number | null {
  const row = record.lastFiveTransactions?.[0];
  return row?.source === LAND_RATE_SOURCE ? positive(row.price) ?? null : null;
}

/**
 * Records store the property type they were valued as. Older records predate
 * that, so recover it from what the flow sent: land-led flows send the Land
 * Rate row, and plots vs houses are told apart by the configuration (which
 * falls back to the property type).
 */
export function categoryFromRecord(record: CiRecord): PropertyCategory {
  if (record.propertyCategory) return record.propertyCategory;
  if (landRateFromRecord(record) !== null) {
    return /house|villa/i.test(record.configuration ?? '') ? 'independent-house' : 'plot-land';
  }
  return record.listingUnitName ? 'resale' : 'primary-project';
}

/** Land/Building step values, sent only for the flows that show those steps. */
function landLedValues(value: CiFormValue): Pick<CalculateCiRequest, 'landValue' | 'buildingValue'> {
  if (value.category !== 'plot-land' && value.category !== 'independent-house') {
    return { landValue: null, buildingValue: null };
  }
  const l = value.landValue;
  const b = value.buildingValue;
  // Until the override has both a total and a reason it is not sent, so the
  // calculated land value keeps showing; switched off, it clears on the backend.
  const override = !!l.supplyOverride && isSupplyOverrideComplete(l);
  return {
    landValue: {
      landRatePsf: positive(l.landRatePsf) ?? null,
      roadWidthFt: positive(l.roadWidthFt) ?? null,
      cornerPlot: !!l.cornerPlot,
      frontageFt: positive(l.frontageFt) ?? null,
      zoning: l.zoning || null,
      orientation: l.orientation || null,
      roadAdjustment: numberOrNull(l.roadAdjustment) ?? 0,
      premiumAdjustment: numberOrNull(l.premiumAdjustment) ?? 0,
      supplyOverride: override,
      supplyOverrideValue: override ? positive(l.supplyOverrideValue) ?? null : null,
      supplyOverrideReason: override ? l.supplyOverrideReason.trim() : null,
    },
    buildingValue:
      value.category === 'independent-house'
        ? {
            builtUpArea: positive(b.builtUpArea) ?? null,
            ageYears: numberOrNull(b.ageYears),
            constructionClass: b.constructionClass || null,
            buildingRatePsf: needsBuildingRate(numberOrNull(b.ageYears))
              ? positive(b.buildingRatePsf) ?? null
              : null,
            floors: numberOrNull(b.floors),
          }
        : null,
  };
}

/**
 * The current calculate endpoint requires one rate record. Plot and house
 * flows do not have a transaction ladder, so pass their Land Rate as the
 * internal benchmark input without presenting it as a user transaction.
 */
function landBenchmarkInput(value: CiFormValue): TransactionRow[] {
  const rate = positive(value.landValue.landRatePsf);
  if (!rate) return lastFiveTransactions(value);

  const rows: TransactionRow[] = [
    {
      price: rate,
      source: LAND_RATE_SOURCE,
      date: toApiDate(new Date().toISOString().slice(0, 10)),
    },
  ];
  while (rows.length < MAX_TRANSACTION_ROWS) rows.push({ price: null, source: null, date: null });
  return rows;
}

const rupees = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
const formatRupees = (n: number) => `₹${rupees.format(Math.round(n))}`;

/**
 * Form → CalculateCiRequest. Returns null until size, quoted price and at least
 * one priced transaction (or Land Rate) exist, since the endpoint rejects a
 * request without them. Every key is present;
 * blank optional fields go as null, matching the API's documented payload.
 */
export function buildCalculateRequest(
  value: CiFormValue,
  questions: CiQuestion[],
  userId?: string | null,
): CalculateCiRequest | null {
  const p = value.property;
  const size = positive(p.size);
  const quotedUnitPrice = positive(p.quotedPrice);
  if (!size || !quotedUnitPrice) return null;

  const transactions =
    value.category === 'plot-land' || value.category === 'independent-house'
      ? landBenchmarkInput(value)
      : lastFiveTransactions(value);
  if (!transactions.some((r) => r.price != null)) return null;

  // Plots and houses are valued on land (+ building); the apartment checklist does not apply.
  const answers = isLandLed(value.category) ? {} : value.benchmark.answers ?? {};
  const currentUdsRate = positive(value.udsRental.currentRatePsf);
  return {
    userId: userId || undefined,
    propertyId: p.propertyId || undefined,
    propertyName: p.propertyName || null,
    microMarket: p.microMarket || null,
    cluster: p.cluster || null,
    configuration: p.configuration || null,
    listingUnitName: p.listingUnitName || null,
    propertyCategory: value.category,
    ...landLedValues(value),
    quotedPricePerSqFt: formatRupees(quotedUnitPrice / size),
    quotedPriceOfUnit: formatRupees(quotedUnitPrice),
    quotedUnitPrice,
    size,
    lastFiveTransactions: transactions,
    uds: positive(value.udsRental.udsSqft) ?? null,
    currentRatePerSqFtForUDS: currentUdsRate
      ? formatRupees(currentUdsRate)
      : null,
    currentUdsRatePerSqft: currentUdsRate ?? null,
    averageRentPerMonth: positive(value.udsRental.monthlyRent) ?? null,
    trophyOverride: !!value.udsRental.trophyOverride,
    locality:
      toApiValue('locality', selectedLabel(questions, answers, CHECKLIST_MATCHERS.locality)) ?? null,
    builderTier:
      toApiValue('builderTier', selectedLabel(questions, answers, CHECKLIST_MATCHERS.builderTier)) ?? null,
    legalIssueStatus:
      toApiValue('legalIssueStatus', selectedLabel(questions, answers, CHECKLIST_MATCHERS.legalIssueStatus)) ??
      null,
    scarcity:
      toApiValue('scarcity', selectedLabel(questions, answers, CHECKLIST_MATCHERS.scarcity)) ?? null,
    samePropertyOverpricing:
      toApiValue('samePropertyOverpricing', selectedLabel(questions, answers, CHECKLIST_MATCHERS.samePropertyOverpricing)) ?? null,
  };
}

/** Fills unanswered checklist fields with zero-weight values for previews and draft calculations. */
export function withNeutralChecklist(req: CalculateCiRequest): CalculateCiRequest {
  if (req.propertyCategory === 'plot-land' || req.propertyCategory === 'independent-house') return req;
  return {
    ...req,
    locality: req.locality || LocalityRating.AVERAGE,
    builderTier: req.builderTier || BuilderTier.AVERAGE,
    legalIssueStatus: req.legalIssueStatus || LegalIssueStatus.CLEAR_TITLE_OC_PENDING,
    scarcity: req.scarcity || ScarcityRating.NORMAL,
    samePropertyOverpricing: req.samePropertyOverpricing || SamePropertyOverpricingRating.IN_LINE,
  };
}

/**
 * Everything standing between the form and a complete calculation, in words:
 * missing basics, unanswered checklist questions, and questions or options the
 * CI API would not recognise (which leave that weightage blank).
 */
export function calculationIssues(value: CiFormValue, questions: CiQuestion[]): string[] {
  const issues: string[] = [];
  const p = value.property;

  if (!p.propertyName) issues.push('Select a property on step 1.');
  if (!positive(p.size)) issues.push('Size is required.');
  if (!positive(p.quotedPrice)) issues.push('Quoted price is required.');
  if (isLandLed(value.category)) return issues.concat(landIssues(value));
  if (usableTransactions(value).length === 0) {
    issues.push('Add at least one benchmark record with a price and date.');
  }

  const answers = value.benchmark.answers ?? {};
  for (const field of Object.keys(CHECKLIST_MATCHERS) as (keyof typeof CHECKLIST_MATCHERS)[]) {
    const question = questions.find((q) => CHECKLIST_MATCHERS[field].test(q.label));
    if (!question) {
      issues.push(`No "${CHECKLIST_NAMES[field]}" question was found in the CI questions.`);
      continue;
    }
    const label = toApiValue(field, selectedLabel(questions, answers, CHECKLIST_MATCHERS[field]));
    if (!label) {
      issues.push(`Answer "${question.label}" on the checklist step.`);
    } else if (!CHECKLIST_VALUES[field].includes(label)) {
      issues.push(
        `"${label}" (${question.label}) is not a value the CI API accepts. Expected: ${CHECKLIST_VALUES[field].join(', ')}.`,
      );
    }
  }
  return issues;
}

/** What a plot / house still needs before its CI value is complete. */
function landIssues(value: CiFormValue): string[] {
  const issues: string[] = [];
  const l = value.landValue;
  if (!positive(l.landRatePsf)) issues.push('Circled Land Rate is required on the Land Value step.');
  if (!l.orientation) issues.push('Select the plot orientation on the Adjustments step.');
  if (l.supplyOverride) {
    if (!positive(l.supplyOverrideValue)) issues.push('Enter the Supply Override land value (₹) on the Adjustments step.');
    if (!l.supplyOverrideReason?.trim()) issues.push('Enter the reason for the Supply Override on the Adjustments step.');
  }
  if (value.category === 'independent-house') {
    const b = value.buildingValue;
    if (!positive(b.builtUpArea)) issues.push('Built-up area is required on the Building Value step.');
    if (numberOrNull(b.ageYears) === null) issues.push('Building age is required on the Building Value step.');
    if (!b.constructionClass) issues.push('Construction classification is required on the Building Value step.');
    if (needsBuildingRate(numberOrNull(b.ageYears)) && !positive(b.buildingRatePsf)) {
      issues.push('Pick the building rate for a 13–15 year old building.');
    }
  }
  return issues;
}

/** Stable identity for a request — decides whether a stored result is still fresh. */
export function requestKey(request: CalculateCiRequest | null): string | null {
  return request ? JSON.stringify(request) : null;
}

/** Backend result → what the benchmark steps display. No arithmetic here. */
export function basisFromResult(
  result: CiCalculationResult | null,
  value: CiFormValue,
  manualRate: number | null,
): BenchmarkBasis {
  const source = value.benchmarking.source;
  const sampleSize = usableTransactions(value).length;

  if (source !== 'transactions') {
    // No ladder to derive from — the analyst-entered rate is the benchmark.
    return {
      baseBenchmarkPsf: manualRate && manualRate > 0 ? manualRate : 0,
      source,
      sampleSize: 0,
      last5AveragePsf: null,
      latestTransactionPsf: null,
      latestTransactionDate: null,
      latestTransactionAgeMonths: null,
      derivedFromComps: false,
    };
  }

  return {
    baseBenchmarkPsf: result?.benchmarkPsf ?? 0,
    source,
    sampleSize,
    last5AveragePsf: result?.last5TransactionsAvgPsf ?? null,
    latestTransactionPsf: result?.lastTransactionPsf ?? null,
    latestTransactionDate: result?.dateOfLastTransactedPrice ?? null,
    latestTransactionAgeMonths: result?.lastTransactionAgeMonths ?? null,
    derivedFromComps: !!result,
  };
}

/** Display tone for a backend verdict string. Styling only. */
export function verdictTone(verdict: string | null | undefined): VerdictTone {
  const v = (verdict ?? '').toLowerCase();
  if (/legal|aggressive/.test(v)) return 'risk';
  if (/negotiate/.test(v)) return 'warn';
  if (/attractive|good|land-led/.test(v)) return 'value';
  return 'neutral';
}
